import React, { useState, useEffect } from "react";
import GoogleMapReact from "google-map-react";
import MainLayout from "layout/MainLayout";
import axios from "utils/axios";
import { toast } from "react-toastify";
import { getPhoneNumberMask } from "utils/utils";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import PhoneInTalkIcon from "@mui/icons-material/PhoneInTalk";
import Marker from "./Marker";
import { useParams } from "react-router-dom";

const MapSearch = () => {
  const [searchResult, setSearchResult] = useState<Array<any>>([]);
  const [center, setCenter] = useState<any>({
    lat: 39.299236,
    lng: -76.609383,
  });
  const [locations, setLocations] = useState<Array<any>>([]);
  const [zoom, setZoom] = useState<number>(13);
  const [open, setOpen] = useState<boolean>(true);
  const [currentLocation, setCurrentLocation] = useState<any>([
    {
      lat: 0,
      lng: 0,
    },
  ]);
  const [activeLocation, setActiveLocation] = useState<number>(-1);

  const { name } = useParams();

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(function (position) {
        setCurrentLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
      });
    } else {
      toast.error("Geolocation is not supported");
    }
    loadResources(name as string);
  }, [name]); //eslint-disable-line

  const getLocationInfo = async (item: any) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: item.address }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          resolve({
            lat: results?.[0].geometry.location.lat(),
            lng: results?.[0].geometry.location.lng(),
            type: item.type,
            address: item.address,
          });
        } else {
          resolve(null);
        }
      });
    });
  };

  const loadResources = async (search: string) => {
    axios.get(
      `/api/search/addKeyword/${search === "all" ? "all resources" : search}`
    );
    try {
      const result = await axios.post(`/api/resource/search`, {
        name: search === "all" ? "" : search,
      });

      let array: Array<any> = [];
      for (let item of result.data) {
        const lRes = await getLocationInfo(item);
        if (lRes) {
          array.push(lRes);
        }
      }
      setLocations(array);
      setSearchResult(result.data);
    } catch (error: any) {
      toast.error(error.response.data.message);
    }
  };

  const handleOpen = () => {
    const { innerWidth: width } = window;
    if (width < 640) setOpen(!open);
  };

  const handleClick = (resourceInfo: any) => {
    const { address } = resourceInfo;
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: address }, (result, status) => {
      if (status === google.maps.GeocoderStatus.OK) {
        setCenter({
          lat: result?.[0].geometry.location.lat(),
          lng: result?.[0].geometry.location.lng(),
        });
      }
    });
    setZoom(15);
    const { innerWidth: width } = window;
    if (width < 640) setOpen(false);
  };

  const handleColor = (name: string) => {
    switch (name) {
      case "Food":
        return "purple";
      case "Legal":
        return "yellow";
      case "Sleep":
        return "green";
      case "Education":
        return "sky";
      default:
        return "red";
    }
  };

  return (
    <MainLayout title="Gyde Search">
      <div className="h-[calc(100vh-230px)] sm:h-[calc(100vh-280px)] grow relative z-0 flex">
        <div
          className={`relative sm:absolute w-[320px] sm:w-full h-full ${
            open ? "sm:h-full" : "sm:h-0"
          } transition-all duration-300`}
        >
          <div
            className="relative sm:absolute top-0 w-[320px] sm:w-full z-50 uppercase bg-[#F0F0F0] px-[12px] py-[8px] flex justify-between items-center cursor-pointer"
            onClick={handleOpen}
          >
            <span>{name} in Baltimore, MD</span>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </div>
          <div
            className={`absolute z-40 top-0 pt-10 left-0 w-[320px] sm:w-full h-full bg-background overflow-scroll ${
              open ? "sm:h-full" : "sm:h-0"
            } transition-all duration-300`}
          >
            <div className="cursor-default">
              {searchResult.map((item, index: number) => (
                <div
                  className={`p-[12px] border-solid ${
                    index === activeLocation
                      ? "border-[3px] border-[#f00]"
                      : "border-[1.5px] border-[#C4C4C4]"
                  }`}
                  key={index}
                >
                  <h5
                    className="font-mont font-medium text-lg break-all hover:underline hover:text-[#3498db] cursor-pointer"
                    onClick={() => window.open(`/view-resource?id=${item.id}`)}
                  >
                    {item.name}
                  </h5>
                  <div
                    className="flex gap-[8px] hover:text-[#3498db] cursor-pointer"
                    onClick={() => handleClick(item)}
                  >
                    <LocationOnIcon />
                    {item.address}
                  </div>
                  <div className="flex gap-[8px] hover:text-[#3498db] cursor-pointer">
                    <PhoneInTalkIcon />
                    <a
                      href={`tel:${item.phoneNumber}`}
                      rel="noopener noreferrer"
                    >
                      {getPhoneNumberMask(item.phoneNumber)}
                    </a>
                  </div>
                  {item.email && (
                    <a
                      className="flex gap-[8px] hover:text-[#3498db] cursor-pointer"
                      href={`mailto:${item.email}`}
                    >
                      <ForwardToInboxIcon />
                      {item.email}
                    </a>
                  )}
                  {item.websiteLink && (
                    <div
                      className="flex gap-[8px] hover:text-[#3498db] cursor-pointer"
                      onClick={() =>
                        window.open(`${item.websiteLink}`, "_blank")
                      }
                    >
                      <TravelExploreIcon />
                      {item.websiteLink}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="relative z-10 w-full">
          <GoogleMapReact
            bootstrapURLKeys={{
              key: "AIzaSyDmuSJ3FBq3DvAtgPcB0dgzCV_u7kROVrE",
            }}
            center={center}
            zoom={zoom}
          >
            {locations.map((item: any, index: number) => (
              <Marker
                key={index}
                lat={item.lat}
                lng={item.lng}
                name={item.address}
                color={handleColor(item.type)}
                zIndex="100"
                onClick={() => setActiveLocation(index)}
              />
            ))}
            <Marker
              lat={currentLocation.lat}
              lng={currentLocation.lng}
              name="My Current Location"
              color="blue"
              zIndex="40"
            />
          </GoogleMapReact>
        </div>
      </div>
    </MainLayout>
  );
};

export default MapSearch;
